import { ChartData, ChartDataset, ChartOptions } from 'chart.js';
import React from 'react';
import {
    AUDIENCE_ANALYTICS_COLORS,
    AUDIENCE_MEASURE_CONFIG,
    EAudienceMeasureType,
    formatAudienceAnalyticsChartDate,
    formatAudienceAnalyticsMeasureValue,
    parseAudienceMeasureKey
} from './model';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { UseCubeQueryResult } from '@cubejs-client/react';
import { useCurrencyString } from 'lib/hooks/useCurrencyString';
import { Series } from '@cubejs-client/core';

interface SeriesWithLoading {
    series: Series<any>;
    loading: boolean;
}

export function useAudienceAnalyticsChart(
    activeUsersChartQueries: UseCubeQueryResult<any, any>[],
    audienceChartQueries: UseCubeQueryResult<any, any>[],
    selectedMeasure: EAudienceMeasureType,
    audiencesConfig: Record<
        string,
        {
            title: string;
            color: string;
        }
    >
) {
    const { audiences } = useSelector((state: ApplicationState) => state.audiences);
    const getCurrencyString = useCurrencyString();
    const activeUsersSeries = React.useMemo(() => {
        const list: SeriesWithLoading[] = [];
        activeUsersChartQueries.forEach(activeUsersChartQuery => {
            activeUsersChartQuery.resultSet?.series()?.forEach(item => {
                list.push({ series: item, loading: activeUsersChartQuery.isLoading });
            });
        });
        return list;
    }, [activeUsersChartQueries]);
    const audienceSeries = React.useMemo(() => {
        const list: SeriesWithLoading[] = [];
        audienceChartQueries.forEach(audienceChartQuery => {
            audienceChartQuery.resultSet?.series()?.forEach(item => {
                list.push({ series: item, loading: audienceChartQuery.isLoading });
            });
        });
        return list;
    }, [audienceChartQueries]);
    const activeUsersChartSeries = React.useMemo<{
        labels: string[];
        activeUsersDataset?: ChartDataset<'line', any[]>;
    }>(() => {
        const currentSeries = activeUsersSeries.find(item => {
            const [_, measure] = parseAudienceMeasureKey(item.series.key);
            return measure === selectedMeasure;
        });
        if (!currentSeries) {
            return {
                labels: [],
                activeUsersDataset: undefined
            };
        }
        return {
            labels: currentSeries.series.series.map(formatAudienceAnalyticsChartDate),
            activeUsersDataset: {
                fill: false,
                label: 'Active Users',
                data: currentSeries.series.series.map(item => item.value),
                cubicInterpolationMode: 'monotone',
                borderColor: AUDIENCE_ANALYTICS_COLORS[0],
                backgroundColor: AUDIENCE_ANALYTICS_COLORS[0],
                tension: 0.4
            }
        };
    }, [activeUsersSeries, selectedMeasure]);

    const chartData = React.useMemo<ChartData<'line', any[], string>>(() => {
        const { labels, activeUsersDataset } = activeUsersChartSeries;
        if (audienceSeries.length === 0 && !activeUsersDataset) {
            return { labels: [], datasets: [] };
        }
        const datasets: ChartDataset<'line', any[]>[] = [];
        if (activeUsersDataset) {
            datasets.push(activeUsersDataset);
        }
        const currentAudienceSeires = audienceSeries.filter(item => {
            const [_, key] = item.series.key.split(',');
            const [__, measure] = parseAudienceMeasureKey(key);
            return measure === selectedMeasure;
        });
        currentAudienceSeires.forEach(item => {
            const [audienceId] = item.series.key.split(',');
            datasets.push({
                fill: false,
                label: audiences.find(audience => audience._id === audienceId)?.title || audienceId,
                data: item.series.series.map(({ value }) => value),
                cubicInterpolationMode: 'monotone',
                borderColor: audiencesConfig[audienceId]?.color,
                backgroundColor: audiencesConfig[audienceId]?.color,
                tension: 0.4
            });
        });
        return {
            labels,
            datasets
        };
    }, [activeUsersChartSeries, audienceSeries, audiences, audiencesConfig, selectedMeasure]);
    const scales = React.useMemo(
        () => ({
            y: {
                ticks: {
                    callback: (value: number) =>
                        formatAudienceAnalyticsMeasureValue(
                            value,
                            AUDIENCE_MEASURE_CONFIG[selectedMeasure].format,
                            getCurrencyString
                        )
                },
                title: {
                    display: true,
                    text: AUDIENCE_MEASURE_CONFIG[selectedMeasure].title
                }
            }
        }),
        [getCurrencyString, selectedMeasure]
    );
    const getTooltipLabelCallback = React.useCallback(
        context => {
            let label = context.dataset.label || '';

            if (label) {
                label += ': ';
            }
            const config = AUDIENCE_MEASURE_CONFIG[selectedMeasure];
            if (context.parsed.y !== null) {
                label += formatAudienceAnalyticsMeasureValue(
                    context.parsed.y,
                    config?.format,
                    getCurrencyString
                );
            }
            return label;
        },
        [getCurrencyString, selectedMeasure]
    );

    const chartOptions = React.useMemo<ChartOptions<'line'>>(
        () => ({
            plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                    callbacks: {
                        label: getTooltipLabelCallback
                    }
                }
            },
            scales,
            maintainAspectRatio: false,
            interaction: {
                intersect: false,
                mode: 'index'
            }
        }),
        [getTooltipLabelCallback, scales]
    );
    const loading = React.useMemo(
        () =>
            activeUsersSeries.find(item => {
                const [_, measure] = parseAudienceMeasureKey(item.series.key);
                return measure === selectedMeasure;
            })?.loading ||
            audienceSeries.find(item => {
                const [_, measure] = parseAudienceMeasureKey(item.series.key);
                return measure === selectedMeasure;
            })?.loading,
        [activeUsersSeries, audienceSeries, selectedMeasure]
    );
    const error = React.useMemo(
        () =>
            activeUsersChartQueries.find(item => !!item.error) ||
            audienceChartQueries.find(item => !!item.error)?.error,
        [activeUsersChartQueries, audienceChartQueries]
    );
    return {
        chartData,
        chartOptions,
        chartLoading: loading,
        chartError: error
    };
}
